import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { LocalizationService } from '../../core/localization/localization.service';
import { CurrencySelectorComponent } from '../../shared/design-system/currency-selector.component';
import { CustomerSelectorComponent } from '../../shared/design-system/customer-selector.component';
import { AinAmountPipe } from '../../shared/design-system/ain-amount.pipe';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { REGULATED_PANEL_STYLES } from './sales-regulated-panel.styles';
import { RegulatedGoldQuoteListItem, SalesRegulatedApiService } from './sales-regulated-api.service';

@Component({
  standalone: true,
  selector: 'ain-sales-gold-quote-detail',
  imports: [
    AinPageComponent, AinStateComponent, RouterLink, FormsModule, ButtonModule,
    InputTextModule, InputNumberModule, CustomerSelectorComponent, CurrencySelectorComponent, AinAmountPipe, AinDatePipe
  ],
  template: `
    <ain-page [title]="isCreate() ? t('sales.regulated.gold.newQuote') : t('sales.regulated.gold.quoteDetailTitle')" [description]="t('sales.regulated.gold.quotesDescription')">
      @if (loading()) { <ain-state type="loading" [title]="t('common.loading')" /> } @else {
        <header class="card"><a class="back-link" [routerLink]="['/sales/gold-quotes']">{{ t('catalog.action.backToList') }}</a></header>
        @if (isCreate()) {
          <section class="card grid">
            <label><span>{{ t('sales.regulated.gold.fields.metal') }}</span><input pInputText [(ngModel)]="form.metalCode" /></label>
            <label><span>{{ t('sales.regulated.gold.fields.bid') }}</span><p-inputNumber [(ngModel)]="form.bidPrice" [min]="0" /></label>
            <label><span>{{ t('sales.regulated.gold.fields.ask') }}</span><p-inputNumber [(ngModel)]="form.askPrice" [min]="0" /></label>
            <label><span>{{ t('sales.installments.fields.currency') }}</span><ain-currency-selector [(ngModel)]="form.currencyCode" /></label>
            <label><span>{{ t('sales.regulated.gold.fields.validMinutes') }}</span><p-inputNumber [(ngModel)]="form.validForMinutes" [min]="1" /></label>
            <label class="full"><span>{{ t('sales.orders.fields.customer') }}</span><ain-customer-selector [(ngModel)]="form.customerAccountId" /></label>
            <button pButton type="button" [label]="t('common.save')" (click)="save()"></button>
          </section>
        } @else if (quote()) {
          <section class="summary card">
            <article><span>{{ t('sales.regulated.gold.fields.metal') }}</span><strong>{{ quote()!.metalCode }}</strong></article>
            <article><span>{{ t('sales.regulated.gold.fields.bid') }}</span><strong>{{ quote()!.bidPrice | ainAmount }} {{ quote()!.currencyCode }}</strong></article>
            <article><span>{{ t('sales.regulated.gold.fields.ask') }}</span><strong>{{ quote()!.askPrice | ainAmount }} {{ quote()!.currencyCode }}</strong></article>
            <article><span>{{ t('sales.regulated.gold.fields.validUntil') }}</span><strong>{{ quote()!.validUntilUtc | ainDate:'datetime' }}</strong></article>
          </section>
          <button pButton type="button" [label]="t('sales.regulated.gold.newOrder')" (click)="newOrderFromQuote()"></button>
        }
      }
    </ain-page>
  `,
  styles: [REGULATED_PANEL_STYLES]
})
export class SalesGoldQuoteDetailComponent implements OnInit {
  private readonly api = inject(SalesRegulatedApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);
  readonly loading = signal(true);
  readonly isCreate = signal(false);
  readonly quote = signal<RegulatedGoldQuoteListItem | null>(null);
  form = { metalCode: 'XAU', bidPrice: 0, askPrice: 0, currencyCode: 'IRR', validForMinutes: 15, customerAccountId: null as string | null };
  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('quoteId') ?? '';
    this.isCreate.set(!id || id === 'new');
    if (this.isCreate()) { this.loading.set(false); return; }
    this.api.getGoldQuote(id).subscribe({
      next: q => { this.quote.set(q); this.loading.set(false); },
      error: () => this.loading.set(false)
    });
  }
  save(): void {
    this.api.createGoldQuote(this.form).subscribe({
      next: r => void this.router.navigate(['/sales/gold-quotes', r.catalogQuoteId])
    });
  }
  newOrderFromQuote(): void {
    const q = this.quote();
    if (!q) return;
    void this.router.navigate(['/sales/gold-orders/new'], { queryParams: { quoteId: q.id } });
  }
  t(key: string): string { return this.localization.translate(key); }
}
